<script>
import { ref, provide, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
// composable
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useScrollOwnership } from 'dashboard/composables/useScrollOwnership';
import { useAlert } from 'dashboard/composables';

// components
import ReplyBox from './ReplyBox.vue';
import SugestaoDaAndie from 'dashboard/components-next/captain/SugestaoDaAndie.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MessageSelectionToolbar from './MessageSelectionToolbar.vue';
import ForwardConversationModal from './ForwardConversationModal.vue';
import ResizableEditorWrapper from './ResizableEditorWrapper.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export default {
  components: {
    MessageList,
    ReplyBox,
    SugestaoDaAndie,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    MessageSelectionToolbar,
    ForwardConversationModal,
    ResizableEditorWrapper,
  },
  mixins: [inboxMixin],
  setup() {
    const conversationPanelRef = ref(null);
    const resizableEditorWrapperRef = ref(null);
    const messagesViewRef = useTemplateRef('messagesViewRef');
    const topBannerRef = useTemplateRef('topBannerRef');
    const { height: containerHeight } = useElementSize(messagesViewRef);
    const { height: topBannerHeight } = useElementSize(topBannerRef);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    // Quem decide se um evento de `scroll` foi do usuário ou nosso.
    const { markProgrammaticScroll, isProgrammaticScroll } =
      useScrollOwnership();

    return {
      markProgrammaticScroll,
      isProgrammaticScroll,
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      resizableEditorWrapperRef,
      messagesViewRef,
      topBannerRef,
      containerHeight,
      topBannerHeight,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      recentlyLoaded: false,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isNearBottom: true,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      showForwardModal: false,
      showSearch: false,
      searchQuery: '',
      searchMatchIndex: 0,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isSelectionMode() {
      return this.$store.getters['messageSelection/isSelectionMode'];
    },
    selectedMessageCount() {
      return this.$store.getters['messageSelection/selectedMessageCount'];
    },
    isForwarding() {
      return this.$store.getters['messageSelection/getUIFlags'].isForwarding;
    },
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    searchMatches() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return [];
      return this.getMessages.filter(
        m =>
          typeof m.content === 'string' && m.content.toLowerCase().includes(q)
      );
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    messagesCount() {
      return this.getMessages.length;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    showScrollToBottomButton() {
      return this.hasUserScrolled && !this.isNearBottom;
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      // MessagesView não remonta ao trocar de conversa (mesma instância pro
      // painel inteiro) — sem isso, isLoadingPrevious/recentlyLoaded ficam
      // com o valor deixado pela conversa anterior (ex: fetch de scroll que
      // ainda estava em voo quando o usuário trocou de chat) e o spinner de
      // "carregando mensagens antigas" fica órfão pra sempre na conversa nova,
      // já que nada mais dispara um fetchPreviousMessages nela pra zerar a flag.
      this.isLoadingPrevious = false;
      this.recentlyLoaded = false;
      // Sem isso, trocar de conversa depois de ter rolado pra cima na
      // anterior deixava `hasUserScrolled` preso em `true` (instância do
      // componente persiste entre conversas) — a nova conversa nem tentava
      // ir pro fim, achando que era "o usuário que rolou".
      this.hasUserScrolled = false;
      this.isNearBottom = true;
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.resetReplyEditorHeight();
      this.assentarPainel();
    },
    // Corrida de tempo achada ao vivo em produção (2026-07-30, Victor):
    // `scrollToBottom()` podia rodar ANTES da mensagem-alvo existir no DOM
    // (dados ainda chegando da API/websocket) — `document.getElementById`
    // voltava null, caía no fallback `scrollTop = scrollHeight` medido
    // num painel ainda quase vazio, e nada corrigia depois. Qualquer
    // mudança na CONTAGEM de mensagens da conversa atual reaplica o
    // scroll — cobre esse timing sem precisar acertar a ordem exata de
    // quem populou o quê primeiro. `hasUserScrolled` protege contra
    // sobrescrever uma rolagem manual do usuário (e contra reprocessar
    // mensagens antigas carregadas via `fetchPreviousMessages`, que já
    // seta `hasUserScrolled = true` antes de disparar).
    messagesCount() {
      if (this.hasUserScrolled) return;

      this.$nextTick(() => this.scrollToBottom());
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    emitter.on(BUS_EVENTS.TOGGLE_CONVERSATION_SEARCH, this.toggleSearch);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    // TROCAR DE CONVERSA SEM CLARÃO (24/10/2026). O painel não remonta (ver o watch de
    // currentChat): as mensagens da conversa nova apareciam de uma vez no lugar das da
    // anterior. Aqui o painel assenta — sobe 4px e acende em 0,3 s. Pela Web Animations
    // API, no elemento, sem tocar na lista (a rolagem dela tem regras próprias). Sem
    // animação para quem pede menos movimento.
    assentarPainel() {
      const painel =
        this.conversationPanelRef?.$el || this.conversationPanelRef;
      if (!painel?.animate) return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
        return;
      painel.animate(
        [
          { opacity: 0.35, transform: 'translate3d(0, 4px, 0)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 300, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      emitter.off(BUS_EVENTS.TOGGLE_CONVERSATION_SEARCH, this.toggleSearch);
    },
    // ── Busca por mensagens (na conversa carregada) ──────────────────────────
    toggleSearch() {
      this.showSearch = !this.showSearch;
      if (this.showSearch) {
        this.$nextTick(() => this.$refs.conversationSearchInput?.focus());
      } else {
        this.clearSearch();
      }
    },
    closeSearch() {
      this.showSearch = false;
      this.clearSearch();
    },
    clearSearch() {
      this.searchQuery = '';
      this.searchMatchIndex = 0;
      this.removeSearchHighlight();
    },
    onSearchInput() {
      this.searchMatchIndex = 0;
      if (this.searchMatches.length) this.goToMatch(0);
      else this.removeSearchHighlight();
    },
    nextMatch() {
      if (!this.searchMatches.length) return;
      this.searchMatchIndex =
        (this.searchMatchIndex + 1) % this.searchMatches.length;
      this.goToMatch(this.searchMatchIndex);
    },
    prevMatch() {
      if (!this.searchMatches.length) return;
      this.searchMatchIndex =
        (this.searchMatchIndex - 1 + this.searchMatches.length) %
        this.searchMatches.length;
      this.goToMatch(this.searchMatchIndex);
    },
    removeSearchHighlight() {
      document
        .querySelectorAll('.advup-search-hit')
        .forEach(el => el.classList.remove('advup-search-hit'));
    },
    goToMatch(index) {
      const match = this.searchMatches[index];
      if (!match) return;
      this.$nextTick(() => {
        const el = document.getElementById('message' + match.id);
        if (!el) return;
        this.markProgrammaticScroll();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.removeSearchHighlight();
        el.classList.add('advup-search-hit');
      });
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.markProgrammaticScroll();
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      // Achado ao vivo em produção (2026-07-30): mesmo mirando na mensagem
      // certa, `scrollIntoView` roda ANTES de anexos (foto/documento)
      // grandes terminarem de carregar — quando a imagem carrega e cresce,
      // o layout empurra tudo pra baixo e ninguém corrige de novo. `load`
      // não borbulha, por isso o listener vai na fase de CAPTURA no
      // painel (delega pra qualquer <img> filha, presente ou futura).
      this.conversationPanel.addEventListener(
        'load',
        this.handleAttachmentLoad,
        true
      );
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
      this.conversationPanel.removeEventListener(
        'load',
        this.handleAttachmentLoad,
        true
      );
    },
    handleAttachmentLoad(e) {
      if (e.target.tagName !== 'IMG' || this.hasUserScrolled) return;

      this.scrollToBottom();
    },
    // Achado real em produção (2026-07-30, relato Dra. Leticia + Victor):
    // as classes `.message--read`/`.message--unread` usadas aqui foram
    // removidas de `Message.vue` num refactor upstream antigo —
    // `querySelectorAll` sempre voltava vazio, `calculateScrollTop`
    // recebia um array vazio e devolvia um scrollTop sem relação nenhuma
    // com o fim da conversa (geralmente negativo, o browser fazia clamp
    // pra 0 — abria no TOPO do histórico).
    //
    // 1º fix (esta função calculando certo, mas mirando no PRIMEIRO
    // não-lido) ainda deixava o mesmo sintoma pra quem nunca tinha visto a
    // conversa: `agent_last_seen_at` nulo/antigo faz TODA a conversa
    // contar como não-lida, e o primeiro não-lido pode ser literalmente a
    // primeira mensagem do histórico inteiro. Corrigido pra sempre mirar
    // no ÚLTIMO evento — igual WhatsApp/Digisac, sempre abre no fim.
    // Usa o ID real via `getReadMessages`/`getUnreadMessages` (baseados em
    // `agent_last_seen_at`, não classe CSS) + `scrollIntoView`, mesmo
    // padrão já usado em `goToMatch`/`onScrollToMessage` neste arquivo.
    scrollToBottom() {
      this.markProgrammaticScroll();

      const labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');
      let targetElement = labelSuggestions || null;

      if (!targetElement) {
        const lastMessage = this.getMessages[this.getMessages.length - 1];
        targetElement = lastMessage
          ? document.getElementById(`message${lastMessage.id}`)
          : null;
      }

      if (targetElement) {
        targetElement.scrollIntoView({ block: 'end' });
      } else {
        // Conversa ainda carregando/vazia — sem mensagem-alvo pra mirar,
        // vai pro fim físico do painel mesmo.
        this.conversationPanel.scrollTop = this.conversationPanel.scrollHeight;
      }
    },
    // Handler do botão flutuante "voltar pro fim" (pedido: paridade com a
    // seta do Digisac). Sempre vai pro ÚLTIMO evento da conversa, não pro
    // primeiro não-lido — é uma ação explícita do usuário, diferente do
    // scrollToBottom() automático ao abrir a conversa.
    scrollToLatestMessage() {
      this.markProgrammaticScroll();
      this.conversationPanel.scrollTo({
        top: this.conversationPanel.scrollHeight,
        behavior: 'smooth',
      });
      this.makeMessagesRead();
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        !this.recentlyLoaded &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        this.recentlyLoaded = true;
        const heightBeforeLoad = this.conversationPanel.scrollHeight;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          this.$nextTick(() => {
            const heightDiff =
              this.conversationPanel.scrollHeight - heightBeforeLoad;
            if (heightDiff > 0) {
              this.conversationPanel.scrollTop += heightDiff;
            }
          });
        } catch (error) {
          // Falhar aqui é invisível de um jeito perigoso: o advogado rola pra
          // cima, não vem nada, e conclui que a conversa COMEÇA ali. Num
          // processo, "o cliente nunca me disse isso" baseado num histórico
          // que só não carregou é o pior tipo de erro silencioso.
          useAlert(this.$t('CONVERSATION.LOAD_PREVIOUS_FAILED'));
        } finally {
          this.isLoadingPrevious = false;
          setTimeout(() => {
            this.recentlyLoaded = false;
          }, 500);
        }
      }
    },

    handleScroll(e) {
      this.hasUserScrolled = !this.isProgrammaticScroll();
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Botão "voltar pro fim" aparece só depois que o usuário rolou pra
      // cima de verdade e ainda está a mais de ~1 mensagem do fim real.
      this.isNearBottom = scrollHeight - (scrollTop + clientHeight) < 120;
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(scrollTop);
    },

    makeMessagesRead() {
      this.$store.dispatch('markMessagesRead', { id: this.currentChat.id });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    handleForwardMessage(messageId) {
      this.$store.dispatch('messageSelection/toggleSelectionMode', true);
      this.$store.dispatch('messageSelection/selectMessage', messageId);
      this.showForwardModal = true;
    },
    handleEnterSelectionMode(messageId) {
      this.$store.dispatch('messageSelection/toggleSelectionMode', true);
      this.$store.dispatch('messageSelection/selectMessage', messageId);
    },
    handleOpenForwardModal() {
      this.showForwardModal = true;
    },
    handleCloseForwardModal() {
      this.showForwardModal = false;
    },
    handleCancelSelection() {
      this.$store.dispatch('messageSelection/exitSelectionMode');
    },
    async handleForwardToConversations(conversationIds) {
      try {
        await this.$store.dispatch(
          'messageSelection/forwardMessages',
          conversationIds
        );
        this.showForwardModal = false;
        this.$store.dispatch('messageSelection/exitSelectionMode');
      } catch {
        // Error is handled in the store
      }
    },
    toggleReplyEditorSize() {
      this.resizableEditorWrapperRef?.toggleEditorExpand?.();
    },
    resetReplyEditorHeight() {
      this.resizableEditorWrapperRef?.resetEditorHeight?.();
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewRef"
    class="flex flex-col justify-between flex-grow h-full min-w-0 m-0 relative"
  >
    <div class="advup-marca-dagua absolute inset-0 pointer-events-none z-0" />
    <div ref="topBannerRef">
      <!-- WhatsApp oficial: o aviso da janela mora acima do campo de resposta
           (JanelaDeResposta), com o que fazer — não esta faixa genérica. -->
      <Banner
        v-if="!currentChat.can_reply && !currentChat.janela_whatsapp"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="replyWindowBannerMessage"
        :href-link="replyWindowLink"
        :href-link-text="replyWindowLinkText"
      />
      <Banner
        v-else-if="hasDuplicateInstagramInbox"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
      />
    </div>
    <!-- Busca dentro da conversa: a casca é a do produto (`.advup-busca`), que
         flutua sobre as mensagens. Os três botões tinham `title=` (legenda
         nativa, que aparece tarde) e nenhum nome para leitor de tela. -->
    <Transition name="mv-busca">
      <div v-if="showSearch" class="advup-busca mv-busca">
        <i class="advup-busca__lupa i-lucide-search" aria-hidden="true" />
        <input
          ref="conversationSearchInput"
          v-model="searchQuery"
          type="text"
          class="advup-busca__campo reset-base"
          :placeholder="$t('CONVERSATION.MESSAGE_SEARCH.PLACEHOLDER')"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.PLACEHOLDER')"
          @input="onSearchInput"
          @keydown.enter.prevent="nextMatch"
          @keydown.esc.prevent="closeSearch"
        />
        <span v-if="searchQuery" class="mv-busca__conta" aria-live="polite">
          {{ searchMatches.length ? searchMatchIndex + 1 : 0 }}/{{
            searchMatches.length
          }}
        </span>
        <span class="mv-busca__fio" aria-hidden="true" />
        <button
          v-tooltip.top="$t('CONVERSATION.MESSAGE_SEARCH.PREV')"
          type="button"
          class="advup-acao advup-acao--p"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.PREV')"
          :disabled="!searchMatches.length"
          @click="prevMatch"
        >
          <i class="i-lucide-chevron-up" />
        </button>
        <button
          v-tooltip.top="$t('CONVERSATION.MESSAGE_SEARCH.NEXT')"
          type="button"
          class="advup-acao advup-acao--p"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.NEXT')"
          :disabled="!searchMatches.length"
          @click="nextMatch"
        >
          <i class="i-lucide-chevron-down" />
        </button>
        <button
          v-tooltip.top="$t('CONVERSATION.MESSAGE_SEARCH.CLOSE')"
          type="button"
          class="advup-acao advup-acao--p"
          :aria-label="$t('CONVERSATION.MESSAGE_SEARCH.CLOSE')"
          @click="closeSearch"
        >
          <i class="i-lucide-x" />
        </button>
      </div>
    </Transition>
    <div class="relative flex-shrink flex-grow basis-px min-h-0 min-w-0">
      <MessageList
        ref="conversationPanelRef"
        class="conversation-panel flex flex-col overflow-y-auto relative z-[1] h-full m-0 pb-4"
        :current-user-id="currentUserId"
        :first-unread-id="unReadMessages[0]?.id"
        :is-an-email-channel="isAnEmailChannel"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :messages="getMessages"
        @retry="handleMessageRetry"
        @forward="handleForwardMessage"
        @enter-selection-mode="handleEnterSelectionMode"
      >
        <template #beforeAll>
          <!-- Era um `<transition name="slide-up">` sem nada que alternasse nem CSS
               (removido em 24/10/2026): não animava nada. -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-blue-11" />
          </li>
        </template>
        <template #unreadBadge>
          <!-- Onde começa o que a pessoa ainda não leu: uma FAIXA de ponta a
               ponta, e não só uma pílula solta — é a linha que o olho procura
               ao abrir a conversa. -->
          <li v-show="unreadMessageCount != 0" class="mv-naolidas">
            <span class="mv-naolidas__pilula">
              <i
                class="i-lucide-arrow-down mv-naolidas__ico"
                aria-hidden="true"
              />
              {{ unreadMessageLabel }}
            </span>
          </li>
        </template>
        <template #after>
          <Transition name="advup-altura">
            <ConversationLabelSuggestion
              v-if="shouldShowLabelSuggestions"
              :suggested-labels="labelSuggestions"
              :chat-labels="currentChat.labels"
              :conversation-id="currentChat.id"
            />
          </Transition>
        </template>
      </MessageList>
      <!-- Paridade Digisac: seta pra voltar pro fim da conversa quando o
           usuário rolou pra cima. Fica fora do <ul> scrollável de propósito
           (absolute no wrapper, não no conteúdo) pra não se mover junto do
           scroll. -->
      <Transition name="mv-fim">
        <button
          v-show="showScrollToBottomButton"
          v-tooltip.left="$t('CONVERSATION.SCROLL_TO_BOTTOM')"
          type="button"
          class="mv-fim"
          :aria-label="$t('CONVERSATION.SCROLL_TO_BOTTOM')"
          @click="scrollToLatestMessage"
        >
          <i class="i-lucide-chevron-down mv-fim__ico" aria-hidden="true" />
          <!-- Quantas chegaram enquanto a pessoa lia lá em cima: é o motivo
               de voltar. -->
          <!-- A chave é o número: cada mensagem que chega enquanto a pessoa lê lá em
               cima faz o selo saltar (24/10/2026), como o contador da lista. -->
          <Transition name="mv-fim-conta">
            <span
              v-if="unreadMessageCount > 0"
              :key="unreadMessageCount"
              class="mv-fim__conta"
            >
              {{ unreadMessageCount > 99 ? '99+' : unreadMessageCount }}
            </span>
          </Transition>
        </button>
      </Transition>
    </div>
    <div class="flex relative flex-col bg-n-surface-1">
      <!-- O "digitando" sobe da caixa de resposta e volta para ela (24/10/2026). -->
      <Transition name="mv-digitando">
        <div
          v-if="isAnyoneTyping"
          class="absolute z-20 flex items-center w-full h-0 -top-7"
        >
          <!-- "Digitando" em três pontos desenhados, e não num GIF: o GIF era
             branco (sumia no tema escuro), tinha `alt` em inglês e não
             respeitava o movimento reduzido. -->
          <div class="mv-digitando" role="status">
            <span class="mv-digitando__nome">{{ typingUserNames }}</span>
            <span class="mv-digitando__pontos" aria-hidden="true">
              <i /><i /><i />
            </span>
          </div>
        </div>
      </Transition>
      <ResizableEditorWrapper
        ref="resizableEditorWrapperRef"
        :container-height="Math.max(0, containerHeight - topBannerHeight)"
      >
        <!-- advup — modo assistido: a resposta que a Andie preparou, para aprovar. -->
        <SugestaoDaAndie :messages="getMessages" />
        <ReplyBox @toggle-editor-size="toggleReplyEditorSize" />
      </ResizableEditorWrapper>
    </div>

    <!-- Message Selection Toolbar -->
    <MessageSelectionToolbar
      v-if="isSelectionMode"
      :selected-count="selectedMessageCount"
      :is-forwarding="isForwarding"
      @forward="handleOpenForwardModal"
      @cancel="handleCancelSelection"
    />

    <!-- Forward Conversation Modal -->
    <ForwardConversationModal
      :show="showForwardModal"
      :is-forwarding="isForwarding"
      @close="handleCloseForwardModal"
      @forward="handleForwardToConversations"
    />
  </div>
</template>

<style scoped>
/* Papel de parede da conversa.
 *
 * Estava em `opacity: 0.3` com o ladrilho a 55% da largura: os desenhos e a
 * palavra "adv up!" ficavam do tamanho das bolhas e no mesmo peso do texto
 * das mensagens — dava para LER o fundo, e ler o fundo é competir com a
 * conversa, que é a única coisa que importa nesta tela.
 *
 * Ele FICA — é branding nosso, pedido explícito do Victor. O que muda é o
 * peso: marca d'água é para ser SENTIDA, não lida.
 *
 * O ajuste que resolve NÃO é a opacidade — é o TAMANHO do ladrilho.
 *
 * A 55% da largura os desenhos ficavam do tamanho das bolhas e a palavra
 * "adv up!" saía do tamanho do texto das mensagens: dava para LER o fundo, e
 * ler o fundo é competir com a conversa. A 32% o mesmo padrão, repetido
 * menor, lê como TRAMA de papel timbrado — e aí a opacidade original volta a
 * caber, porque não há mais nenhum traço grande o bastante para disputar com
 * uma linha de texto.
 *
 * Por isso a opacidade fica nos 0.30 de sempre. O caminho até aqui foi ida e
 * volta com o Victor: 0.055 (sumiu), 0.12 ("muito apagado"), 0.16, 0.20,
 * 0.26 e 0.34 comparados na tela. Com o ladrilho pequeno, 0.30 é presença de
 * marca sem ruído — que era o objetivo desde o começo.
 *
 * No escuro a arte é clara sobre quase-preto e o olho enxerga bem mais
 * contraste com a mesma opacidade — por isso ela cai um degrau a mais lá.
 */
/* A lista de mensagens não tem fundo próprio (ver MessageList): assim a marca
   d'água (`.advup-marca-dagua`, z-0) aparece atrás. */
.conversation-panel {
  overflow-anchor: none;
}

/* ── Busca dentro da conversa ─────────────────────────────────────────── */
.mv-busca {
  position: absolute;
  z-index: 20;
  top: var(--esp-2);
  left: 50%;
  width: min(92%, 440px);
  max-width: none;
  background: rgb(var(--solid-1));
  box-shadow: 0 8px 24px -10px rgb(15 23 42 / 28%);
  translate: -50% 0;
}

.mv-busca__conta {
  flex-shrink: 0;
  padding-inline: var(--esp-1);
  font-size: var(--txt-nota);
  font-weight: var(--peso-rotulo);
  color: rgb(var(--slate-11));
  font-variant-numeric: tabular-nums;
}

.mv-busca__fio {
  width: 1px;
  height: 20px;
  flex-shrink: 0;
  background: rgb(var(--slate-5));
}

.mv-busca-enter-active,
.mv-busca-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva);
}

.mv-busca-enter-from,
.mv-busca-leave-to {
  opacity: 0;
  translate: -50% -8px;
}

/* ── Não lidas ────────────────────────────────────────────────────────── */
.mv-naolidas {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  margin: var(--esp-3) 0;
  list-style: none;
}

.mv-naolidas::before,
.mv-naolidas::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgb(var(--blue-9) / 35%);
}

.mv-naolidas__pilula {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: var(--esp-1) var(--esp-3);
  border-radius: var(--r-redondo);
  background: rgb(var(--blue-9));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(255 255 255);
  box-shadow: 0 1px 2px rgb(15 23 42 / 14%);
}

.mv-naolidas__ico {
  width: var(--ico-1);
  height: var(--ico-1);
}

/* ── Voltar ao fim ────────────────────────────────────────────────────── */
.mv-fim {
  position: absolute;
  z-index: 20;
  right: var(--esp-4);
  bottom: var(--esp-4);
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
  box-shadow: 0 8px 24px -10px rgb(15 23 42 / 30%);
  cursor: pointer;
  transition:
    color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva);
}

.mv-fim:hover {
  border-color: rgb(var(--blue-8));
  color: rgb(var(--blue-11));
  translate: 0 -2px;
}

.mv-fim:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.mv-fim__ico {
  width: var(--ico-2);
  height: var(--ico-2);
}

.mv-fim__conta {
  position: absolute;
  top: -6px;
  right: -4px;
  display: grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 var(--esp-1);
  border: 2px solid rgb(var(--solid-1));
  border-radius: var(--r-redondo);
  background: rgb(var(--blue-9));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-forte);
  line-height: 1;
  color: rgb(255 255 255);
  font-variant-numeric: tabular-nums;
}

.mv-fim-conta-enter-active {
  animation: mv-fim-conta var(--mov-3) var(--mov-curva);
}

@keyframes mv-fim-conta {
  from {
    opacity: 0;
    transform: translate3d(0, 4px, 0) scale(0.7);
  }
}

.mv-fim-enter-active,
.mv-fim-leave-active {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo);
}

.mv-fim-enter-from,
.mv-fim-leave-to {
  opacity: 0;
  scale: 0.6;
}

/* ── Digitando ────────────────────────────────────────────────────────── */
.mv-digitando {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  margin: var(--esp-2) auto;
  padding: var(--esp-2) var(--esp-3) var(--esp-2) var(--esp-4);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-redondo);
  background: rgb(var(--solid-1));
  box-shadow: 0 8px 24px -10px rgb(15 23 42 / 24%);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-11));
}

/* A raiz também transiciona: o Vue mede a duração pela raiz, e sem nada nela a saída da
   pílula seria cortada no primeiro quadro. */
.mv-digitando-enter-active,
.mv-digitando-enter-active .mv-digitando {
  transition:
    opacity var(--mov-2) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva);
}

.mv-digitando-leave-active,
.mv-digitando-leave-active .mv-digitando {
  transition:
    opacity var(--mov-2) var(--mov-curva-saida),
    translate var(--mov-2) var(--mov-curva-saida);
}

.mv-digitando-enter-from,
.mv-digitando-leave-to {
  opacity: 0;
}

.mv-digitando-enter-from .mv-digitando,
.mv-digitando-leave-to .mv-digitando {
  translate: 0 6px;
}

.mv-digitando__pontos {
  display: inline-flex;
  gap: var(--esp-1);
}

.mv-digitando__pontos > i {
  width: 6px;
  height: 6px;
  border-radius: var(--r-redondo);
  background: rgb(var(--blue-9));
  animation: mv-pula calc(var(--mov-3) * 4) var(--mov-curva) infinite;
}

.mv-digitando__pontos > i:nth-child(2) {
  animation-delay: var(--mov-1);
}

.mv-digitando__pontos > i:nth-child(3) {
  animation-delay: var(--mov-3);
}

@keyframes mv-pula {
  0%,
  60%,
  100% {
    opacity: 0.35;
    translate: 0 0;
  }

  30% {
    opacity: 1;
    translate: 0 -3px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mv-digitando__pontos > i {
    animation: none;
    opacity: 0.7;
  }

  .mv-fim,
  .mv-fim:hover {
    translate: none;
    transition: none;
  }

  .mv-fim-conta-enter-active {
    animation: none;
  }
}
</style>

<style>
/* highlight da busca: realce suave SÓ no balão (não na linha inteira) */
.advup-search-hit [data-bubble-name] {
  /* Era `#fbbf24` cravado: um âmbar que não é o nosso e que fica igual no
     tema escuro. O âmbar do produto é o mesmo do prazo e do alerta. */
  box-shadow:
    0 0 0 2px rgb(var(--amber-9)),
    0 2px 10px rgb(var(--amber-9) / 0.35);
  transition: box-shadow var(--mov-2) var(--mov-curva);
}
</style>
